import {
  base64UrlDecode,
  base64UrlEncode,
  concatBytes,
  decodeUtf8,
  encodeUtf8,
  sha256Base64Url,
} from "./bytes";
import { resolveTransferLimits, type TransferLimits } from "../packaging/limits";
import { isValidSessionId, QR_PROTOCOL_VERSION } from "./invitation";
import {
  createDataSegmentRanges,
  MAX_SEGMENTABLE_DATA_FRAMES,
  type DataSegmentRange,
} from "./segmentation";

export const DEFAULT_DATA_CHUNK_BYTES = 320;
export const DEFAULT_METADATA_CHUNK_BYTES = 280;
export const MAX_QR_FRAME_TEXT_LENGTH = 4_096;
const MAX_FRAME_PAYLOAD_BYTES = 1_024;
const SHA256_LENGTH = 43;

export type TransferFrameKind = "metadata" | "data";

type WireFrameKind = "m" | "d";

interface WireFrame {
  v: 1;
  t: WireFrameKind;
  s: string;
  i: number;
  n: number;
  m: number;
  c: number;
  z: number;
  p: string;
  h: string;
  d: string;
}

export interface TransferFrame {
  version: 1;
  kind: TransferFrameKind;
  sessionId: string;
  index: number;
  total: number;
  metadataFrameCount: number;
  dataFrameCount: number;
  packageByteLength: number;
  packageSha256: string;
  chunkSha256: string;
  payload: Uint8Array;
  id: string;
  text: string;
}

export interface TransferDescriptor {
  version: 1;
  packageByteLength: number;
  packageSha256: string;
  fileCount: number;
}

export interface CreateTransferFramesOptions {
  dataChunkBytes?: number;
  metadataChunkBytes?: number;
  limits?: Partial<TransferLimits>;
}

export interface TransferFrameSet {
  sessionId: string;
  packageSha256: string;
  packageByteLength: number;
  descriptor: TransferDescriptor;
  metadataFrames: TransferFrame[];
  dataFrames: TransferFrame[];
  frames: TransferFrame[];
}

export class FrameValidationError extends Error {
  public constructor(
    message: string,
    public readonly code:
      "malformed" | "unsupported-version" | "invalid-session" | "bounds" | "corrupt",
  ) {
    super(message);
    this.name = "FrameValidationError";
  }
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return (
    typeof value === "object" &&
    value !== null &&
    !Array.isArray(value) &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

function assertChunkSize(name: string, value: number, maximum: number): void {
  if (!Number.isSafeInteger(value) || value <= 0 || value > maximum) {
    throw new Error(`${name} must be between 1 and ${maximum.toString()}.`);
  }
}

function splitBytes(bytes: Uint8Array, chunkSize: number): Uint8Array[] {
  if (bytes.byteLength === 0) {
    return [new Uint8Array()];
  }

  const chunks: Uint8Array[] = [];
  for (let offset = 0; offset < bytes.byteLength; offset += chunkSize) {
    chunks.push(bytes.slice(offset, offset + chunkSize));
  }
  return chunks;
}

function kindToWire(kind: TransferFrameKind): WireFrameKind {
  return kind === "metadata" ? "m" : "d";
}

function wireToKind(kind: WireFrameKind): TransferFrameKind {
  return kind === "m" ? "metadata" : "data";
}

function canonicalWireFrame(frame: WireFrame): string {
  return JSON.stringify({
    v: frame.v,
    t: frame.t,
    s: frame.s,
    i: frame.i,
    n: frame.n,
    m: frame.m,
    c: frame.c,
    z: frame.z,
    p: frame.p,
    h: frame.h,
    d: frame.d,
  });
}

export async function encodeTransferFrame(input: {
  kind: TransferFrameKind;
  sessionId: string;
  index: number;
  total: number;
  metadataFrameCount: number;
  dataFrameCount: number;
  packageByteLength: number;
  packageSha256: string;
  payload: Uint8Array;
}): Promise<TransferFrame> {
  if (!isValidSessionId(input.sessionId)) {
    throw new Error("Cannot encode a frame with an invalid session ID.");
  }
  if (
    !Number.isSafeInteger(input.index) ||
    input.index < 0 ||
    !Number.isSafeInteger(input.total) ||
    input.total <= 0 ||
    input.index >= input.total
  ) {
    throw new Error("Cannot encode a frame with invalid index bounds.");
  }
  if (
    !Number.isSafeInteger(input.metadataFrameCount) ||
    input.metadataFrameCount <= 0 ||
    !Number.isSafeInteger(input.dataFrameCount) ||
    input.dataFrameCount <= 0 ||
    input.dataFrameCount > MAX_SEGMENTABLE_DATA_FRAMES ||
    !Number.isSafeInteger(input.packageByteLength) ||
    input.packageByteLength <= 0 ||
    input.packageSha256.length !== SHA256_LENGTH ||
    input.payload.byteLength > MAX_FRAME_PAYLOAD_BYTES
  ) {
    throw new Error("Cannot encode a frame with invalid transfer bounds.");
  }

  const chunkSha256 = await sha256Base64Url(input.payload);
  const wire: WireFrame = {
    v: QR_PROTOCOL_VERSION,
    t: kindToWire(input.kind),
    s: input.sessionId,
    i: input.index,
    n: input.total,
    m: input.metadataFrameCount,
    c: input.dataFrameCount,
    z: input.packageByteLength,
    p: input.packageSha256,
    h: chunkSha256,
    d: base64UrlEncode(input.payload),
  };
  const text = canonicalWireFrame(wire);
  if (text.length > MAX_QR_FRAME_TEXT_LENGTH) {
    throw new Error("Encoded frame is too dense for the configured QR policy.");
  }

  return {
    version: QR_PROTOCOL_VERSION,
    kind: input.kind,
    sessionId: input.sessionId,
    index: input.index,
    total: input.total,
    metadataFrameCount: input.metadataFrameCount,
    dataFrameCount: input.dataFrameCount,
    packageByteLength: input.packageByteLength,
    packageSha256: input.packageSha256,
    chunkSha256,
    payload: input.payload.slice(),
    id: `${kindToWire(input.kind)}:${input.index.toString()}:${chunkSha256}`,
    text,
  };
}

function parseWireFrame(text: string): WireFrame {
  if (text.length === 0 || text.length > MAX_QR_FRAME_TEXT_LENGTH) {
    throw new FrameValidationError(
      "Frame text length is outside the supported range.",
      "bounds",
    );
  }

  let value: unknown;
  try {
    value = JSON.parse(text) as unknown;
  } catch {
    throw new FrameValidationError("Frame is not valid JSON.", "malformed");
  }

  if (!isPlainObject(value)) {
    throw new FrameValidationError("Frame is not an object.", "malformed");
  }
  if (value.v !== QR_PROTOCOL_VERSION) {
    throw new FrameValidationError(
      "Frame uses an unsupported protocol version.",
      "unsupported-version",
    );
  }
  if (value.t !== "m" && value.t !== "d") {
    throw new FrameValidationError("Frame type is invalid.", "malformed");
  }

  const stringKeys = ["s", "p", "h", "d"] as const;
  const numberKeys = ["i", "n", "m", "c", "z"] as const;
  for (const key of stringKeys) {
    if (typeof value[key] !== "string") {
      throw new FrameValidationError(`Frame field ${key} is invalid.`, "malformed");
    }
  }
  for (const key of numberKeys) {
    if (!Number.isSafeInteger(value[key])) {
      throw new FrameValidationError(`Frame field ${key} is invalid.`, "malformed");
    }
  }

  return {
    v: QR_PROTOCOL_VERSION,
    t: value.t,
    s: value.s as string,
    i: value.i as number,
    n: value.n as number,
    m: value.m as number,
    c: value.c as number,
    z: value.z as number,
    p: value.p as string,
    h: value.h as string,
    d: value.d as string,
  };
}

export async function decodeTransferFrame(
  text: string,
  overrides: Partial<TransferLimits> = {},
): Promise<TransferFrame> {
  const limits = resolveTransferLimits(overrides);
  const wire = parseWireFrame(text);
  if (!isValidSessionId(wire.s)) {
    throw new FrameValidationError("Frame session ID is invalid.", "invalid-session");
  }

  const currentTotal = wire.t === "m" ? wire.m : wire.c;
  const maximumPackageBytes = limits.maxExpandedBytes + limits.maxMetadataBytes + 12;
  if (
    wire.i < 0 ||
    wire.n !== currentTotal ||
    wire.i >= wire.n ||
    wire.m <= 0 ||
    wire.m > limits.maxChunkCount ||
    wire.c <= 0 ||
    wire.c > limits.maxChunkCount ||
    wire.c > MAX_SEGMENTABLE_DATA_FRAMES ||
    wire.z <= 0 ||
    wire.z > maximumPackageBytes ||
    wire.p.length !== SHA256_LENGTH ||
    wire.h.length !== SHA256_LENGTH ||
    !/^[A-Za-z0-9_-]+$/u.test(wire.p) ||
    !/^[A-Za-z0-9_-]+$/u.test(wire.h)
  ) {
    throw new FrameValidationError(
      "Frame bounds are outside the configured policy.",
      "bounds",
    );
  }

  let payload: Uint8Array;
  try {
    payload = base64UrlDecode(wire.d);
  } catch {
    throw new FrameValidationError(
      "Frame payload is not valid base64url.",
      "malformed",
    );
  }
  if (
    payload.byteLength > MAX_FRAME_PAYLOAD_BYTES ||
    (wire.t === "d" && payload.byteLength === 0)
  ) {
    throw new FrameValidationError(
      "Frame payload is outside the configured policy.",
      "bounds",
    );
  }
  const actualChunkSha256 = await sha256Base64Url(payload);
  if (actualChunkSha256 !== wire.h) {
    throw new FrameValidationError(
      "Frame chunk digest does not match its payload.",
      "corrupt",
    );
  }

  const kind = wireToKind(wire.t);
  return {
    version: QR_PROTOCOL_VERSION,
    kind,
    sessionId: wire.s,
    index: wire.i,
    total: wire.n,
    metadataFrameCount: wire.m,
    dataFrameCount: wire.c,
    packageByteLength: wire.z,
    packageSha256: wire.p,
    chunkSha256: wire.h,
    payload,
    id: `${wire.t}:${wire.i.toString()}:${wire.h}`,
    text,
  };
}

export async function createTransferFrames(
  input: {
    sessionId: string;
    packageBytes: Uint8Array;
    packageSha256?: string;
    fileCount: number;
  },
  options: CreateTransferFramesOptions = {},
): Promise<TransferFrameSet> {
  const limits = resolveTransferLimits(options.limits);
  const dataChunkBytes = options.dataChunkBytes ?? DEFAULT_DATA_CHUNK_BYTES;
  const metadataChunkBytes = options.metadataChunkBytes ?? DEFAULT_METADATA_CHUNK_BYTES;
  assertChunkSize("dataChunkBytes", dataChunkBytes, MAX_FRAME_PAYLOAD_BYTES);
  assertChunkSize("metadataChunkBytes", metadataChunkBytes, MAX_FRAME_PAYLOAD_BYTES);
  if (!isValidSessionId(input.sessionId)) {
    throw new Error("A valid session ID is required.");
  }
  if (
    !Number.isSafeInteger(input.fileCount) ||
    input.fileCount <= 0 ||
    input.fileCount > limits.maxFileCount
  ) {
    throw new Error("File count is outside the configured policy.");
  }
  const maximumPackageBytes = limits.maxExpandedBytes + limits.maxMetadataBytes + 12;
  if (
    input.packageBytes.byteLength <= 0 ||
    input.packageBytes.byteLength > maximumPackageBytes
  ) {
    throw new Error("Package bytes are outside the configured policy.");
  }

  const packageSha256 =
    input.packageSha256 ?? (await sha256Base64Url(input.packageBytes));
  if ((await sha256Base64Url(input.packageBytes)) !== packageSha256) {
    throw new Error("Provided package digest does not match package bytes.");
  }

  const descriptor: TransferDescriptor = {
    version: QR_PROTOCOL_VERSION,
    packageByteLength: input.packageBytes.byteLength,
    packageSha256,
    fileCount: input.fileCount,
  };
  const descriptorBytes = encodeUtf8(JSON.stringify(descriptor));
  if (descriptorBytes.byteLength > limits.maxMetadataBytes) {
    throw new Error("Transfer descriptor exceeds the metadata limit.");
  }

  const metadataChunks = splitBytes(descriptorBytes, metadataChunkBytes);
  const dataChunks = splitBytes(input.packageBytes, dataChunkBytes);
  if (
    metadataChunks.length > limits.maxChunkCount ||
    dataChunks.length > limits.maxChunkCount ||
    dataChunks.length > MAX_SEGMENTABLE_DATA_FRAMES
  ) {
    throw new Error("Prepared transfer exceeds the configured chunk limit.");
  }

  const common = {
    sessionId: input.sessionId,
    metadataFrameCount: metadataChunks.length,
    dataFrameCount: dataChunks.length,
    packageByteLength: input.packageBytes.byteLength,
    packageSha256,
  };
  const metadataFrames = await Promise.all(
    metadataChunks.map(async (payload, index) =>
      encodeTransferFrame({
        ...common,
        kind: "metadata",
        index,
        total: metadataChunks.length,
        payload,
      }),
    ),
  );
  const dataFrames = await Promise.all(
    dataChunks.map(async (payload, index) =>
      encodeTransferFrame({
        ...common,
        kind: "data",
        index,
        total: dataChunks.length,
        payload,
      }),
    ),
  );

  return {
    sessionId: input.sessionId,
    packageSha256,
    packageByteLength: input.packageBytes.byteLength,
    descriptor,
    metadataFrames,
    dataFrames,
    frames: [...metadataFrames, ...dataFrames],
  };
}

export interface DataSegmentProgress extends DataSegmentRange {
  readonly received: number;
  readonly missing: number;
  readonly complete: boolean;
}

export interface FrameAssemblyProgress {
  metadataReceived: number;
  metadataTotal?: number;
  dataReceived: number;
  dataTotal?: number;
  dataSegments?: readonly DataSegmentProgress[];
  suggestedSegmentIndex?: number;
  uniqueReceived: number;
  uniqueTotal?: number;
  duplicateCount: number;
  rejectedCount: number;
  hasConflict: boolean;
  readyToFinalize: boolean;
}

export type FrameAcceptResult =
  | {
      status: "accepted";
      frame: TransferFrame;
      progress: FrameAssemblyProgress;
    }
  | {
      status: "duplicate";
      frame: TransferFrame;
      progress: FrameAssemblyProgress;
    }
  | {
      status: "rejected";
      reason:
        | "malformed"
        | "unsupported-version"
        | "invalid-session"
        | "wrong-session"
        | "bounds"
        | "corrupt"
        | "conflicting-duplicate"
        | "conflicting-transfer";
      message: string;
      progress: FrameAssemblyProgress;
    };

export class FrameAssemblyError extends Error {
  public constructor(
    message: string,
    public readonly code: "incomplete" | "conflict" | "metadata" | "integrity",
  ) {
    super(message);
    this.name = "FrameAssemblyError";
  }
}

function parseDescriptor(bytes: Uint8Array): TransferDescriptor {
  let value: unknown;
  try {
    value = JSON.parse(decodeUtf8(bytes)) as unknown;
  } catch {
    throw new FrameAssemblyError("Transfer metadata cannot be decoded.", "metadata");
  }
  if (
    !isPlainObject(value) ||
    value.version !== QR_PROTOCOL_VERSION ||
    !Number.isSafeInteger(value.packageByteLength) ||
    typeof value.packageSha256 !== "string" ||
    !Number.isSafeInteger(value.fileCount)
  ) {
    throw new FrameAssemblyError("Transfer metadata is invalid.", "metadata");
  }
  return {
    version: QR_PROTOCOL_VERSION,
    packageByteLength: value.packageByteLength as number,
    packageSha256: value.packageSha256,
    fileCount: value.fileCount as number,
  };
}

interface AssemblyExpectation {
  metadataFrameCount: number;
  dataFrameCount: number;
  packageByteLength: number;
  packageSha256: string;
}

export class TransferFrameAssembler {
  readonly #metadata = new Map<number, TransferFrame>();
  readonly #data = new Map<number, TransferFrame>();
  readonly #limits: TransferLimits;
  #expectation?: AssemblyExpectation;
  #duplicateCount = 0;
  #rejectedCount = 0;
  #hasConflict = false;
  #metadataByteLength = 0;
  #dataByteLength = 0;

  public constructor(
    public readonly sessionId: string,
    limits: Partial<TransferLimits> = {},
  ) {
    if (!isValidSessionId(sessionId)) {
      throw new Error("A valid invited session ID is required.");
    }
    this.#limits = resolveTransferLimits(limits);
  }

  public get progress(): FrameAssemblyProgress {
    const metadataTotal = this.#expectation?.metadataFrameCount;
    const dataTotal = this.#expectation?.dataFrameCount;
    const dataSegments =
      dataTotal === undefined
        ? undefined
        : createDataSegmentRanges(dataTotal).map<DataSegmentProgress>((segment) => {
            let received = 0;
            for (const dataIndex of this.#data.keys()) {
              if (
                dataIndex >= segment.startDataIndex &&
                dataIndex < segment.endDataIndexExclusive
              ) {
                received += 1;
              }
            }
            const missing = segment.total - received;
            return {
              ...segment,
              received,
              missing,
              complete: missing === 0,
            };
          });
    let suggestedSegmentIndex: number | undefined;
    if (!this.#hasConflict && dataSegments !== undefined) {
      for (const segment of dataSegments) {
        if (
          segment.missing > 0 &&
          (suggestedSegmentIndex === undefined ||
            segment.missing > dataSegments[suggestedSegmentIndex]!.missing)
        ) {
          suggestedSegmentIndex = segment.index;
        }
      }
    }
    const uniqueTotal =
      metadataTotal === undefined || dataTotal === undefined
        ? undefined
        : metadataTotal + dataTotal;
    const uniqueReceived = this.#metadata.size + this.#data.size;
    const readyToFinalize =
      !this.#hasConflict &&
      metadataTotal !== undefined &&
      dataTotal !== undefined &&
      this.#metadata.size === metadataTotal &&
      this.#data.size === dataTotal;

    return {
      metadataReceived: this.#metadata.size,
      ...(metadataTotal === undefined ? {} : { metadataTotal }),
      dataReceived: this.#data.size,
      ...(dataTotal === undefined ? {} : { dataTotal }),
      ...(dataSegments === undefined ? {} : { dataSegments }),
      ...(suggestedSegmentIndex === undefined ? {} : { suggestedSegmentIndex }),
      uniqueReceived,
      ...(uniqueTotal === undefined ? {} : { uniqueTotal }),
      duplicateCount: this.#duplicateCount,
      rejectedCount: this.#rejectedCount,
      hasConflict: this.#hasConflict,
      readyToFinalize,
    };
  }

  public get acceptedFrameTexts(): string[] {
    return [
      ...[...this.#metadata.values()].sort((a, b) => a.index - b.index),
      ...[...this.#data.values()].sort((a, b) => a.index - b.index),
    ].map((frame) => frame.text);
  }

  public async accept(text: string): Promise<FrameAcceptResult> {
    let frame: TransferFrame;
    try {
      frame = await decodeTransferFrame(text, this.#limits);
    } catch (error) {
      this.#rejectedCount += 1;
      if (error instanceof FrameValidationError) {
        return {
          status: "rejected",
          reason: error.code,
          message: error.message,
          progress: this.progress,
        };
      }
      return {
        status: "rejected",
        reason: "malformed",
        message: "Frame could not be validated.",
        progress: this.progress,
      };
    }

    if (frame.sessionId !== this.sessionId) {
      this.#rejectedCount += 1;
      return {
        status: "rejected",
        reason: "wrong-session",
        message: "Frame belongs to a different transfer.",
        progress: this.progress,
      };
    }

    const target = frame.kind === "metadata" ? this.#metadata : this.#data;
    const existing = target.get(frame.index);
    if (existing !== undefined) {
      if (existing.text === frame.text) {
        this.#duplicateCount += 1;
        return { status: "duplicate", frame, progress: this.progress };
      }

      this.#rejectedCount += 1;
      this.#hasConflict = true;
      return {
        status: "rejected",
        reason: "conflicting-duplicate",
        message: "A different valid frame reused an accepted frame index.",
        progress: this.progress,
      };
    }

    const incomingExpectation: AssemblyExpectation = {
      metadataFrameCount: frame.metadataFrameCount,
      dataFrameCount: frame.dataFrameCount,
      packageByteLength: frame.packageByteLength,
      packageSha256: frame.packageSha256,
    };
    if (this.#expectation === undefined) {
      this.#expectation = incomingExpectation;
    } else if (
      this.#expectation.metadataFrameCount !== incomingExpectation.metadataFrameCount ||
      this.#expectation.dataFrameCount !== incomingExpectation.dataFrameCount ||
      this.#expectation.packageByteLength !== incomingExpectation.packageByteLength ||
      this.#expectation.packageSha256 !== incomingExpectation.packageSha256
    ) {
      this.#rejectedCount += 1;
      this.#hasConflict = true;
      return {
        status: "rejected",
        reason: "conflicting-transfer",
        message: "Frame transfer metadata conflicts with accepted frames.",
        progress: this.progress,
      };
    }

    if (
      (frame.kind === "metadata" &&
        this.#metadataByteLength + frame.payload.byteLength >
          this.#limits.maxMetadataBytes) ||
      (frame.kind === "data" &&
        this.#dataByteLength + frame.payload.byteLength > frame.packageByteLength)
    ) {
      this.#rejectedCount += 1;
      return {
        status: "rejected",
        reason: "bounds",
        message: "Accepted frame bytes would exceed transfer bounds.",
        progress: this.progress,
      };
    }

    target.set(frame.index, frame);
    if (frame.kind === "metadata") {
      this.#metadataByteLength += frame.payload.byteLength;
    } else {
      this.#dataByteLength += frame.payload.byteLength;
    }
    return { status: "accepted", frame, progress: this.progress };
  }

  public async finalize(): Promise<{
    bytes: Uint8Array;
    descriptor: TransferDescriptor;
    packageSha256: string;
  }> {
    if (this.#hasConflict) {
      throw new FrameAssemblyError(
        "Conflicting frames prevent reconstruction.",
        "conflict",
      );
    }
    const expectation = this.#expectation;
    if (
      expectation === undefined ||
      this.#metadata.size !== expectation.metadataFrameCount ||
      this.#data.size !== expectation.dataFrameCount
    ) {
      throw new FrameAssemblyError(
        "Missing frames prevent reconstruction.",
        "incomplete",
      );
    }

    const metadataBytes = concatBytes(
      [...this.#metadata.values()]
        .sort((a, b) => a.index - b.index)
        .map((frame) => frame.payload),
    );
    const descriptor = parseDescriptor(metadataBytes);
    if (
      descriptor.packageByteLength !== expectation.packageByteLength ||
      descriptor.packageSha256 !== expectation.packageSha256 ||
      descriptor.fileCount <= 0 ||
      descriptor.fileCount > this.#limits.maxFileCount
    ) {
      throw new FrameAssemblyError(
        "Transfer descriptor conflicts with frame metadata.",
        "metadata",
      );
    }

    const bytes = concatBytes(
      [...this.#data.values()]
        .sort((a, b) => a.index - b.index)
        .map((frame) => frame.payload),
    );
    if (bytes.byteLength !== expectation.packageByteLength) {
      throw new FrameAssemblyError(
        "Reconstructed byte length does not match.",
        "integrity",
      );
    }
    if ((await sha256Base64Url(bytes)) !== expectation.packageSha256) {
      throw new FrameAssemblyError("Full package digest does not match.", "integrity");
    }

    return {
      bytes,
      descriptor,
      packageSha256: expectation.packageSha256,
    };
  }
}
